import { track } from "./dispatch.js";
import {
  cartParams,
  CURRENCY,
  itemPayload,
  money,
  viewItemParams,
} from "./payload.js";
import { cartChangeEvent, routePageView, searchEvent } from "./rules.js";
import { purchaseEvent } from "./purchase.js";

export * from "./dispatch.js";
export * from "./payload.js";
export * from "./rules.js";
export * from "./meta.js";
export * from "./purchase.js";

/**
 * Isola a instrumentação do fluxo da loja (CA-16).
 *
 * `track()` já protege cada destino, mas a montagem do payload roda antes
 * dele — e estes pontos de chamada ficam no meio de fluxos que não podem
 * quebrar: o `submitOrder` tem `catch` silencioso (o pedido já existe no
 * backend quando o evento é montado) e as actions do carrinho desligam o
 * `appLoading` depois da chamada.
 */
function protegido(envio, fallback = undefined) {
  try {
    return envio();
  } catch {
    return fallback;
  }
}

function storage(scope) {
  try {
    return scope?.localStorage ?? null;
  } catch {
    return null;
  }
}

/** Ponto de chamada da PDP (CA-2). */
export function trackViewItem(item, scope = globalThis) {
  protegido(() => {
    if (!item?.id) {
      return;
    }
    track("view_item", viewItemParams(item), scope);
  });
}

/** Ponto de chamada das actions de carrinho (CA-3, CA-4, CA-4.1). */
export function trackCartChange(change, scope = globalThis) {
  protegido(() => {
    const evento = cartChangeEvent(change);
    if (!evento) {
      return;
    }
    const item = itemPayload(evento.item, evento.quantity);
    track(evento.name, {
      currency: CURRENCY,
      value: money(item.price * evento.quantity),
      items: [item],
    }, scope);
  });
}

/** Ponto de chamada das telas de carrinho e checkout (CA-5, CA-6, CA-7). */
export function trackCartStage(name, cart, extra, scope = globalThis) {
  protegido(() => {
    if (!cart?.items?.length) {
      return;
    }
    track(name, cartParams(cart, extra), scope);
  });
}

/** Ponto de chamada do `submitOrder` (CA-8, CA-9, CA-10). */
export function trackPurchase(order, scope = globalThis) {
  protegido(() => {
    const evento = purchaseEvent(order, storage(scope));
    if (!evento) {
      return;
    }
    track(evento.name, evento.params, scope);
  });
}

/** Tempo, em ms, sem digitação que caracteriza o termo como consolidado. */
export const JANELA_DIGITACAO = 2000;

/** Ponto de chamada da busca, para um termo já consolidado (CA-11). */
export function trackSearch(term, lastTerm, scope = globalThis) {
  protegido(() => {
    const evento = searchEvent(term, lastTerm);
    if (!evento) {
      return;
    }
    track(evento.name, evento.params, scope);
  });
}

/**
 * Consolida a digitação antes de registrar a busca (CA-11).
 *
 * `input()` é chamado a cada `router.replace` do debounce; o envio só
 * acontece quando a digitação para, e leva o **último** termo — enviar a cada
 * fragmento registraria buscas parciais, e suprimir os refinamentos deixaria
 * no relatório só o primeiro pedaço digitado. `stop()` cancela o que estiver
 * pendente, para a tela não registrar busca depois de fechada.
 *
 * `schedule`/`cancel` são injetáveis para manter o teste determinístico.
 */
export function createSearchTracker(options = {}, scope = globalThis) {
  const {
    delay = JANELA_DIGITACAO,
    schedule = setTimeout,
    cancel = clearTimeout,
  } = options;

  let pendente = null;
  let ultimoEnviado = null;

  return {
    input(term) {
      cancel(pendente);
      pendente = schedule(() => {
        pendente = null;
        protegido(() => {
          const evento = searchEvent(term, ultimoEnviado);
          if (!evento) {
            return;
          }
          ultimoEnviado = evento.params.search_term;
          track(evento.name, evento.params, scope);
        });
      }, delay);
    },

    stop() {
      cancel(pendente);
      pendente = null;
    },
  };
}

/** Ponto de chamada do `router.afterEach` (CA-12). */
export function trackPageView(to, from, options, scope = globalThis) {
  return protegido(() => {
    const evento = routePageView(to, from, options);
    if (!evento) {
      return null;
    }
    track("page_view", evento, scope);
    return evento;
  }, null);
}
